import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, FileText, Users, Inbox, Briefcase, Clock, X, Download, ExternalLink, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, API, formatApiErrorDetail } from "../lib/apiClient";
import { STATUS_OPTIONS, EQUIPMENT } from "../lib/formConfig";

const EQUIP_MAP = Object.fromEntries(EQUIPMENT.map((e) => [e.id, e.label]));
const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-300",
  reviewing: "bg-blue-100 text-blue-800 border-blue-300",
  interviewed: "bg-purple-100 text-purple-800 border-purple-300",
  hired: "bg-[#00331D] text-white border-[#00331D]",
  rejected: "bg-gray-100 text-gray-600 border-gray-300",
};
const EMP_LABEL = { full_time: "Full-Time", part_time: "Part-Time", both: "Both" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gmail, setGmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/applications", { params: filter !== "all" ? { status: filter } : {} }),
        api.get("/applications/stats"),
      ]);
      setApps(a.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadGmail = useCallback(async () => {
    try {
      const r = await api.get("/oauth/gmail/status");
      setGmail(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGmail(); }, [loadGmail]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") { toast.success("Gmail connected"); loadGmail(); }
    if (params.get("gmail") === "error") toast.error("Gmail connection failed. Try again.");
  }, [loadGmail]);

  const connectGmail = async () => {
    try {
      const r = await api.get("/oauth/gmail/auth-url");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start Gmail connect");
    }
  };
  const disconnectGmail = async () => {
    await api.post("/oauth/gmail/disconnect");
    toast.success("Gmail disconnected");
    loadGmail();
  };
  const testGmail = async () => {
    try {
      const r = await api.post("/oauth/gmail/test");
      toast.success(`Test email sent to ${r.data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test failed");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success("Status updated");
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      api.get("/applications/stats").then((r) => setStats(r.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const downloadResume = (id) => {
    const token = localStorage.getItem("lcc_token");
    window.open(`${API}/applications/${id}/resume?auth=${token}`, "_blank");
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const STAT_CARDS = stats ? [
    { label: "Total", value: stats.total, icon: Users },
    { label: "New", value: stats.new, icon: Inbox },
    { label: "Full-Time", value: stats.full_time, icon: Briefcase },
    { label: "Part / Both", value: stats.part_time + stats.both, icon: Clock },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1300px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg text-[#1a1a1a]">Land ClearCo</span>
            <span className="text-[#585858] text-xs uppercase tracking-wider">Hiring Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-[#585858] text-sm">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-[#585858] hover:text-[#00331D] text-sm font-medium transition-colors">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-5 py-8">
        {/* Gmail status banner */}
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="gmail-banner">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#00331D]" />
            {gmail?.connected ? (
              <span className="text-sm text-[#1a1a1a] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Email notifications on — sending from <b>{gmail.email}</b> to <b>{gmail.hiring_inbox}</b>
              </span>
            ) : (
              <span className="text-sm text-[#585858] flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                Email notifications are off. Connect Gmail to email new applications to {gmail?.hiring_inbox || "your inbox"}.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {gmail?.connected ? (
              <>
                <button onClick={testGmail} data-testid="gmail-test-btn" className="text-sm font-semibold text-[#00331D] border border-[#00331D] px-4 py-1.5 rounded-md hover:bg-[#00331D] hover:text-white transition-colors">Send Test</button>
                <button onClick={disconnectGmail} data-testid="gmail-disconnect-btn" className="text-sm text-[#585858] hover:text-red-600 px-3 py-1.5">Disconnect</button>
              </>
            ) : (
              <button onClick={connectGmail} data-testid="gmail-connect-btn" className="text-sm font-semibold bg-[#00331D] text-white px-4 py-1.5 rounded-md hover:bg-[#004d2b] transition-colors">Connect Gmail</button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-cards">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between">
                <c.icon className="w-6 h-6 text-[#00331D]" strokeWidth={1.5} />
                <span className="text-3xl font-bold text-[#1a1a1a]">{c.value}</span>
              </div>
              <p className="text-xs text-[#585858] uppercase tracking-wider mt-2">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[{ id: "all", label: "All" }, ...STATUS_OPTIONS].map((o) => (
            <button key={o.id} data-testid={`filter-${o.id}`} onClick={() => setFilter(o.id)}
              className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-colors ${filter === o.id ? "border-[#00331D] bg-[#00331D] text-white" : "border-gray-300 bg-white text-[#1a1a1a] hover:border-[#00331D]"}`}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="applications-table">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-[#F8F8F8] border-b border-gray-200">
                {["Applicant", "Type", "Experience", "Applied", "Status", ""].map((h) => (
                  <th key={h} className="text-left p-4 font-semibold text-xs uppercase tracking-wider text-[#585858]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-10 text-center text-[#585858]">Loading…</td></tr>
              ) : apps.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-[#585858]" data-testid="empty-state">No applications yet.</td></tr>
              ) : (
                apps.map((a) => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-[#F8F8F8] transition-colors" data-testid={`app-row-${a.id}`}>
                    <td className="p-4">
                      <div className="font-semibold text-[#1a1a1a]">{a.first_name} {a.last_name}</div>
                      <div className="text-sm text-[#585858]">{a.email}</div>
                    </td>
                    <td className="p-4 text-sm text-[#585858]">{EMP_LABEL[a.employment_type] || a.employment_type}</td>
                    <td className="p-4 text-sm text-[#585858]">{a.years_experience}</td>
                    <td className="p-4 text-sm text-[#585858]">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 border rounded-full text-xs font-semibold ${STATUS_STYLES[a.status] || ""}`}>
                        {STATUS_OPTIONS.find((s) => s.id === a.status)?.label || a.status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => setSelected(a)} data-testid={`view-app-${a.id}`} className="text-sm font-semibold text-[#00331D] hover:text-[#004d2b]">View</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {selected && <DetailDrawer app={selected} onClose={() => setSelected(null)} onStatus={changeStatus} onDownload={downloadResume} />}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-gray-100">
      <div className="text-xs text-[#585858] uppercase tracking-wider">{label}</div>
      <div className="text-[#1a1a1a] mt-0.5">{value}</div>
    </div>
  );
}

function DetailDrawer({ app, onClose, onStatus, onDownload }) {
  const a = app;
  const avail = a.availability;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="app-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 flex items-center justify-between border-b border-gray-200">
          <h3 className="text-lg font-bold text-[#1a1a1a]">{a.first_name} {a.last_name}</h3>
          <button onClick={onClose} data-testid="close-drawer-btn" className="text-gray-400 hover:text-[#1a1a1a]"><X className="w-6 h-6" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <div className="text-xs text-[#585858] uppercase tracking-wider mb-2">Update Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button key={s.id} data-testid={`status-btn-${s.id}`} onClick={() => onStatus(a.id, s.id)}
                  className={`px-3 py-1.5 rounded-md border text-xs font-semibold transition-colors ${a.status === s.id ? "border-[#00331D] bg-[#00331D] text-white" : "border-gray-300 bg-white text-[#1a1a1a] hover:border-[#00331D]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-5">
            <Row label="Email" value={a.email} />
            <Row label="Phone" value={a.phone} />
            <Row label="Address" value={`${a.street}, ${a.city}, ${a.state} ${a.zip_code}`} />
            <Row label="Employment Type" value={EMP_LABEL[a.employment_type]} />
            <Row label="Years Experience" value={a.years_experience} />
            <Row label="CDL" value={a.has_cdl ? `Yes — ${a.cdl_class || "Class not specified"}` : "No"} />
            <Row label="Other Licenses" value={a.other_licenses} />
            <Row label="Equipment" value={(a.equipment_experience || []).map((e) => EQUIP_MAP[e] || e).join(", ")} />
          </div>

          {avail && (a.employment_type === "part_time" || a.employment_type === "both") && (
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="text-sm font-semibold text-[#00331D] mb-2">Availability</div>
              <Row label="Days" value={(avail.days || []).join(", ")} />
              <Row label="Times" value={(avail.time_of_day || []).join(", ")} />
              <Row label="Hours / Week" value={avail.hours_per_week} />
              <Row label="Earliest Start" value={avail.earliest_start} />
              <Row label="Notes" value={avail.notes} />
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-5">
            <div className="text-sm font-semibold text-[#00331D] mb-3">References</div>
            {(a.references || []).map((r, i) => (
              <div key={i} className="py-2 border-b border-gray-100 last:border-0">
                <div className="font-semibold text-[#1a1a1a]">{r.name} <span className="font-normal text-[#585858]">— {r.relationship}</span></div>
                <div className="text-sm text-[#585858]">{r.phone}{r.email ? ` · ${r.email}` : ""}</div>
              </div>
            ))}
          </div>

          {a.message && (
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="text-sm font-semibold text-[#00331D] mb-2">Message</div>
              <p className="text-[#1a1a1a]">{a.message}</p>
            </div>
          )}

          {a.resume ? (
            <button onClick={() => onDownload(a.id)} data-testid="download-resume-btn" className="w-full flex items-center justify-center gap-2 bg-[#00331D] text-white font-semibold uppercase tracking-wider px-6 py-3 rounded-md hover:bg-[#004d2b] transition-colors">
              <Download className="w-5 h-5" /> {a.resume.original_filename} <ExternalLink className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex items-center gap-2 text-[#585858] text-sm"><FileText className="w-4 h-4" /> No resume uploaded</div>
          )}
        </div>
      </div>
    </div>
  );
}
